import { useEffect, useState } from 'react';
import { DeleteOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Table, TableProps, DatePicker, Space, message, Popconfirm } from 'antd';
import { getRequest, downloadRequest, deleteRequest, ResponseDataType } from 'api';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface BillErrorDataType {
  id: number;
  description: string;
  row: string;
  type: number;
  createdDate: number;
  mstSchool: string;
  fileName: string;
  errorFilePath: string;
}

interface ResponseInfoType {
  list: BillErrorDataType[];
  total: number;
}

export const CreateBillError = () => {
  const [billList, setBillList] = useState<BillErrorDataType[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [dates, setDates] = useState<[string, string] | null>(null);
  
  const schoolId = localStorage.getItem('schoolId');

  const fetchData = async () => {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      perPage: pageSize.toString(),
      schoolId: schoolId || '',
    });
    
    if (dates && dates[0] && dates[1]) {
      queryParams.append('startDate', dates[0]);
      queryParams.append('endDate', dates[1]);
    }

    const url = `/cms/invoice/list-invoices-error?${queryParams.toString()}`;

    try {
      const response: ResponseDataType<ResponseInfoType> = await getRequest(url);
      if (response && response.code === 200) {
        setBillList(response.info?.list || []);
        setTotal(response.info?.total || 0);
      } else {
        setBillList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, pageSize, schoolId, dates]);

  const handleDelete = async (id: number) => {
    const response = await deleteRequest(`/cms/invoice/delete-error-file/${id}`);
    if (response && response.code === 200) {
      message.success('Xóa lịch sử lỗi thành công!');
      fetchData();
    } else {
      message.error(response?.msg || 'Xóa thất bại!');
    }
  };

  const columns: TableProps<BillErrorDataType>['columns'] = [
    {
      title: 'STT',
      key: 'stt',
      width: '60px',
      render: (_text, _record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: 'Tên file import',
      dataIndex: 'fileName',
      key: 'fileName',
      width: '350px',
    },
    {
      title: 'Thời điểm lỗi',
      dataIndex: 'createdDate',
      key: 'createdDate',
      width: '150px',
      render: (createdDate) =>
        new Date(createdDate).toLocaleString('vi-VN', {
          dateStyle: 'short',
          timeStyle: 'short',
        }),
    },
    {
      title: 'Hành động',
      dataIndex: 'id',
      key: 'action',
      width: '200px',
      render: (id, record) => {
        const handleDownload = () => {
          downloadRequest(`/cms/invoice/download-error-file?id=${id}`, record.fileName || `error_file_${id}.xlsx`);
        };
        return (
          <Space size="middle">
            <span
              onClick={handleDownload}
              style={{ color: '#1890ff', cursor: 'pointer', fontWeight: '500' }}
            >
              Tải file
            </span>
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa bản ghi này không?"
              onConfirm={() => handleDelete(id)}
              okText="Đồng ý"
              cancelText="Hủy"
            >
              <span
                style={{ color: '#ff4d4f', cursor: 'pointer', fontWeight: '500' }}
              >
                Xóa
              </span>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const handleTableChange = (pagination: any) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleDateChange = (val: any, formatString: [string, string]) => {
    setDates(val ? formatString : null);
    setPage(1); // Reset to page 1 on filter
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <RangePicker 
          onChange={handleDateChange} 
          format="YYYY-MM-DD"
          placeholder={['Từ ngày', 'Đến ngày']}
        />
      </div>
      <Table
        columns={columns}
        dataSource={billList}
        rowKey="id"
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};
